<template>
  <div class="flex flex-col">
    <div class="w-full flex items-center justify-center mb-3">
      <div class="relative">
        <NegarMoonWithBackgroundIcon size="72" />

        <span
          v-if="isSymbolForbidden"
          class="absolute bottom-0 right-0 bg-white flex items-center justify-center w-6 h-6 rounded-full"
        >
          <span class="w-3 h-3 bg-critical-primary rounded-full" />
        </span>
      </div>
    </div>

    <NText
      tag="div"
      variant="body"
      weight="bold"
      class="flex justify-center text-purple-primary mb-3"
    >
      {{ t('modules.order.sendOfflineOrder') }}
    </NText>

    <div
      v-if="isSymbolForbidden"
      class="flex flex-col border-neutral-secondary border rounded p-2"
    >
      <NText variant="caption1">
        {{ t('symbol.forbiddenTitle') }}
      </NText>

      <NText variant="caption1">
        {{ t('modules.order.canNotSubmitOrderMessage') }}
      </NText>
    </div>

    <NText
      v-else
      variant="caption1"
      class="border-neutral-secondary border rounded p-2"
    >
      {{ t('modules.order.endTradeTimeMessage') }}
    </NText>

    <div
      class="flex items-center gap-x-2 border-neutral-secondary border rounded p-2 mt-3"
    >
      <NegarAlarmLightIcon />

      <NText variant="caption1" weight="medium">
        {{ t('modules.order.updateValidityDateToSevenDaysMessage') }}
      </NText>
    </div>

    <NRadioGroup v-model="priceType" class="mt-3">
      <div class="flex flex-col gap-y-6 p-3">
        <NRadio
          v-gid="{
            module: 'order',
            screen: 'orderErrorSheet',
            component: 'desiredPrice',
          }"
          :value="OFFLINE_ORDER_PRICE_TYPE_OPTIONS.desiredPrice.value"
          :lable="OFFLINE_ORDER_PRICE_TYPE_OPTIONS.desiredPrice.label"
          class="w-full items-start"
        >
          <div class="flex flex-col">
            <div>
              <NText
                variant="caption1"
                weight="bold"
                class="text-neutral-primary pb-1"
              >
                {{ t('modules.order.priceType.desiredPrice.enteredPrice') }}
              </NText>

              <NumberFormatter #="{ number }" :value="formModel?.price">
                <NText variant="body" weight="medium">
                  {{ number }}
                </NText>
              </NumberFormatter>

              <NText variant="overline">
                {{ t('misc.rial') }}
              </NText>
            </div>

            <i18n-t
              scope="global"
              keypath="modules.order.priceType.desiredPrice.description"
              tag="span"
              class="text-c2 font-normal"
            >
              <template #priceYouSetYourself>
                <NText weight="bold" variant="caption2">
                  {{
                    t(
                      'modules.order.priceType.desiredPrice.priceYouSetYourself'
                    )
                  }}
                </NText>
              </template>
            </i18n-t>
          </div>
        </NRadio>

        <NRadio
          v-gid="{
            module: 'order',
            screen: 'orderErrorSheet',
            component: 'marketPrice',
          }"
          :value="OFFLINE_ORDER_PRICE_TYPE_OPTIONS.marketPrice.value"
          :lable="OFFLINE_ORDER_PRICE_TYPE_OPTIONS.marketPrice.label"
          class="w-full"
        >
          <div class="flex flex-col">
            <NText
              variant="caption1"
              weight="bold"
              class="text-neutral-primary pb-1"
            >
              {{ OFFLINE_ORDER_PRICE_TYPE_OPTIONS.marketPrice.label }}
            </NText>

            <i18n-t
              scope="global"
              keypath="modules.order.priceType.marketPrice.description"
              tag="span"
              class="text-c2 font-normal"
            >
              <template #bestPriceAvailableOnOrders>
                <NText weight="bold" variant="caption2">
                  {{
                    t(
                      'modules.order.priceType.marketPrice.bestPriceAvailableOnOrders'
                    )
                  }}
                </NText>
              </template>
            </i18n-t>
          </div>
        </NRadio>
      </div>
    </NRadioGroup>

    <div class="flex justify-between mt-3">
      <NButton
        variant="fill"
        class="flex-[1] ml-4"
        size="small"
        :loading="isLoadingCreateOfflineOrder || isLoadingAgreements"
        :disabled="isLoadingAgreements || isLoadingCreateOfflineOrder"
        @click="handleSendOrder"
      >
        <NText v-if="type === ORDER_TYPES.buy" variant="body" weight="bold">
          {{ t('modules.order.sendBuyOrder') }}
        </NText>

        <NText v-else variant="body" weight="bold">
          {{ t('modules.order.sendSellOrder') }}
        </NText>
      </NButton>

      <NButton
        variant="plain"
        color="gray"
        class="flex-[1]"
        size="small"
        @click="removeDialog()"
      >
        <NText
          class="text-neutral-secondary-hover"
          variant="body"
          weight="bold"
        >
          {{ t('common.cancel') }}
        </NText>
      </NButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDialog } from '@negar/core';
import { NegarMoonWithBackgroundIcon, NegarAlarmLightIcon } from '@negar/icons';
import { OFFLINE_ORDER_EXECUTION_TYPE, ORDER_TYPES } from '@core/order';
import { TRADING_STATE_CODES, useLiveSymbolQuery } from '@core/symbol';
import { numberFormatter } from '@libs/utils';

import NumberFormatter from '@/components/Number.vue';

import {
  OFFLINE_ORDER_PRICE_TYPE_OPTIONS,
  TCreateMarketOrderArguments,
  useCreateOrder,
  useOrderDialogs,
} from '@/modules/order';

type TOfflineOrderSubmissionDialogPropsType = {
  formModel: {
    quantity?: number | null;
    price?: number | null;
    totalValue?: number | null;
    executedQuantity: number | null;
    validityDate: Date;
  };
  isin: string;
  type: 'buy' | 'sell';
};

const props = defineProps<TOfflineOrderSubmissionDialogPropsType>();

const { t } = useI18n();
const { remove: removeDialog } = useDialog();

const { symbol } = useLiveSymbolQuery(toRef(props, 'isin'));

const priceType = ref(OFFLINE_ORDER_PRICE_TYPE_OPTIONS.desiredPrice.value);

const {
  createOfflineOrder,
  createMarketOrder,
  isLoadingCreateOfflineOrder,
  isLoadingAgreements,
} = useCreateOrder({
  isin: props.isin,
  orderType: props.type,
});

const { openOfflineConfirmDialog } = useOrderDialogs();

const isSymbolForbidden = computed(
  () => symbol.value.instrumentTradingState === TRADING_STATE_CODES.forbidden
);

const handleSendOrder = () => {
  if (priceType.value === OFFLINE_ORDER_PRICE_TYPE_OPTIONS.desiredPrice.value) {
    createOfflineOrder({
      formModel: {
        ...props.formModel,
        orderExecutionType: OFFLINE_ORDER_EXECUTION_TYPE.desired.helium,
      },
      shouldCloseDialog: true,
    });

    return;
  }

  removeDialog();

  openOfflineConfirmDialog({
    instrumentName: symbol.value.instrumentName,
    type: props.type,
    description:
      props.type === 'buy'
        ? t('orders.buyMarketPriceOrderMessage', {
            value: numberFormatter(
              Math.floor(props.formModel.totalValue as number)
            ),
          })
        : t('orders.sellMarketPriceOrderMessage', {
            quantity: numberFormatter(props.formModel.quantity as number),
          }),
    createOfflineOrder: () =>
      createMarketOrder({
        formModel: {
          ...props.formModel,
          orderExecutionType: OFFLINE_ORDER_EXECUTION_TYPE.market.helium,
        } as TCreateMarketOrderArguments['formModel'],
        shouldCloseDialog: true,
        isMarketOrderBasedOnQuantity: props.type === 'sell',
      }),
  });
};
</script>
